import { z } from 'zod'
import { Model } from './architecture'
import type { Market, Markets } from '../shared/domain/Market'
import { DomainError } from './DomainError'
import { ErrorsCode } from './ErrorsCode'

export type Countries = (typeof ALLOWED_COUNTRIES)[number]

export const ALLOWED_COUNTRIES = [
  'aa',
  'ad',
  'ae',
  'af',
  'ag',
  'ai',
  'al',
  'am',
  'an',
  'ao',
  'ar',
  'as',
  'at',
  'au',
  'aw',
  'ax',
  'az',
  'ba',
  'bb',
  'bd',
  'be',
  'bf',
  'bg',
  'bh',
  'bi',
  'bj',
  'bk',
  'bl',
  'bm',
  'bn',
  'bo',
  'bq',
  'br',
  'bs',
  'bt',
  'bv',
  'bw',
  'by',
  'bz',
  'ca',
  'cc',
  'cd',
  'cd',
  'cf',
  'cg',
  'ch',
  'ci',
  'ck',
  'cl',
  'cm',
  'cn',
  'co',
  'cr',
  'cu',
  'cv',
  'cw',
  'cx',
  'cy',
  'cz',
  'de',
  'dj',
  'dk',
  'dm',
  'do',
  'dz',
  'ec',
  'ee',
  'eg',
  'eh',
  'er',
  'es',
  'et',
  'fi',
  'fj',
  'fk',
  'fm',
  'fo',
  'fr',
  'ga',
  'gb',
  'gd',
  'ge',
  'gf',
  'gg',
  'gh',
  'gi',
  'gl',
  'gm',
  'gn',
  'gp',
  'gq',
  'gr',
  'gs',
  'gt',
  'gu',
  'gw',
  'gy',
  'hk',
  'hm',
  'hn',
  'hr',
  'ht',
  'hu',
  'id',
  'ie',
  'il',
  'im',
  'in',
  'io',
  'iq',
  'ir',
  'is',
  'it',
  'je',
  'jm',
  'jo',
  'jp',
  'ke',
  'kg',
  'kh',
  'ki',
  'km',
  'kn',
  'kr',
  'kv',
  'kw',
  'ky',
  'kz',
  'la',
  'lb',
  'lc',
  'li',
  'lk',
  'lr',
  'ls',
  'lt',
  'lu',
  'lv',
  'ly',
  'ma',
  'mc',
  'md',
  'me',
  'mf',
  'mg',
  'mh',
  'mk',
  'ml',
  'mm',
  'mn',
  'mo',
  'mp',
  'mq',
  'mr',
  'ms',
  'mt',
  'mu',
  'mv',
  'mw',
  'mx',
  'my',
  'mz',
  'na',
  'nc',
  'ne',
  'nf',
  'ng',
  'ni',
  'nl',
  'no',
  'np',
  'nr',
  'nu',
  'ny',
  'nz',
  'om',
  'pa',
  'pe',
  'pf',
  'pg',
  'ph',
  'pk',
  'pl',
  'pm',
  'pn',
  'pr',
  'ps',
  'pt',
  'pw',
  'py',
  'qa',
  're',
  'ro',
  'rs',
  'ru',
  'rw',
  'sa',
  'sb',
  'sc',
  'sd',
  'se',
  'sf',
  'sg',
  'sh',
  'si',
  'sj',
  'sk',
  'sl',
  'sm',
  'sn',
  'so',
  'sr',
  'ss',
  'st',
  'sv',
  'sx',
  'sy',
  'sz',
  'tc',
  'td',
  'tf',
  'tg',
  'th',
  'tj',
  'tk',
  'tl',
  'tm',
  'tn',
  'to',
  'tr',
  'tt',
  'tv',
  'tw',
  'tz',
  'ua',
  'ug',
  'uk',
  'um',
  'us',
  'uy',
  'uz',
  'va',
  'vc',
  've',
  'vg',
  'vi',
  'vn',
  'vu',
  'wf',
  'ws',
  'xk',
  'xo',
  'ye',
  'yt',
  'za',
  'zm',
  'zw',
] as const

const FROM_MARKET_TO_COUNTRY: Record<Markets, Countries> = {
  uk: 'gb',
  fr: 'fr',
  us: 'us',
  de: 'de',
  it: 'it',
  es: 'es',
  nl: 'nl',
  be: 'be',
  ca: 'ca',
  dk: 'dk',
  gr: 'gr',
  ie: 'ie',
  no: 'no',
  at: 'at',
  pl: 'pl',
  pt: 'pt',
  ro: 'ro',
  ch: 'ch',
  sk: 'sk',
  za: 'za',
  fi: 'fi',
  se: 'se',
  tr: 'tr',
  au: 'au',
  mx: 'mx',
  br: 'br',
  ee: 'ee',
  hr: 'hr',
  lt: 'lt',
  lv: 'lv',
  si: 'si',
  nz: 'nz',
  hu: 'hu',
  cz: 'cz',
  xg: 'sa',
  ae: 'ae',
  eg: 'eg',
  co: 'us',
  ec: 'ec',
  dz: 'dz',
  sg: 'sg',
}

export function isCountryType(value: string): value is Countries {
  return ALLOWED_COUNTRIES.includes(value as Countries)
}

export const CountryValidation = z.object({
  value: z.enum(ALLOWED_COUNTRIES, { required_error: '[Country] value is required' }),
})

export class Country extends Model {
  static create({ value }: z.infer<typeof CountryValidation>) {
    CountryValidation.parse({ value })
    return new Country(value)
  }

  static empty() {
    return new Country('us')
  }

  static fromMarket({ market }: { market: Market }) {
    if (!market.value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    return Country.create({ value: FROM_MARKET_TO_COUNTRY[market.value] })
  }

  constructor(private readonly _value: Countries) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  toJSON() {
    return { value: this.value }
  }
}
